import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Rocket, 
  Instagram, 
  MessageCircle, 
  DollarSign, 
  CheckCircle2,
  Play,
  RefreshCw
} from 'lucide-react';

const Phase0Tester: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [config, setConfig] = useState<any>(null);

  const API_BASE = 'https://biig-api.b-9f2.workers.dev';

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/phase0/config`);
      const data = await res.json();
      setConfig(data);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const runFullFlow = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/phase0/test/full-flow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: 'test_user_' + Date.now() })
      });
      const data = await res.json();
      setResults(data);
    } catch (error) {
      console.error('Error running flow:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchConfig();
  }, []);

  const stepIcons: Record<number, React.ReactNode> = {
    1: <Instagram className="w-5 h-5 text-purple-400" />,
    2: <MessageCircle className="w-5 h-5 text-blue-400" />,
    3: <MessageCircle className="w-5 h-5 text-green-400" />,
    4: <Rocket className="w-5 h-5 text-orange-400" />,
    5: <DollarSign className="w-5 h-5 text-yellow-400" />,
    6: <CheckCircle2 className="w-5 h-5 text-purple-400" />,
    7: <DollarSign className="w-5 h-5 text-green-400" />,
    8: <CheckCircle2 className="w-5 h-5 text-green-400" />
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-white mb-2">
            Phase 0: Zero-Domain Launch
          </h1>
          <p className="text-gray-300">
            ig-growth-kit v1 • Mock Mode • No real charges
          </p>
        </motion.div>

        {/* Config Display */}
        {config && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-gray-800 rounded-xl p-6 mb-8"
          >
            <h2 className="text-xl font-semibold text-white mb-4">Current Configuration</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <span className="text-gray-400">Mode:</span>
                <span className="ml-2 text-white font-bold">
                  {config.testMode ? 'TEST' : 'LIVE'}
                </span>
              </div>
              <div>
                <span className="text-gray-400">WhatsApp:</span>
                <span className="ml-2 text-white">{config.providers?.whatsapp}</span>
              </div>
              <div>
                <span className="text-gray-400">Instagram:</span>
                <span className="ml-2 text-white">{config.providers?.instagram}</span>
              </div>
            </div>
            <div className="mt-4">
              <h3 className="text-white font-medium mb-2">Offer: {config.offer?.name}</h3>
              <div className="flex gap-4 text-sm">
                <span className="text-green-400">FREE Kit</span>
                <span className="text-yellow-400">$9 Mini Pack</span>
                <span className="text-purple-400">$29/mo Concierge</span>
              </div>
            </div>
          </motion.div>
        )}

        {/* Test Button */}
        <div className="flex justify-center mb-8">
          <button
            onClick={runFullFlow}
            disabled={loading}
            className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-bold text-lg hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 flex items-center gap-3"
          >
            {loading ? (
              <>
                <RefreshCw className="w-6 h-6 animate-spin" />
                Running Test...
              </>
            ) : (
              <>
                <Play className="w-6 h-6" />
                Run Full ig-growth-kit Flow
              </>
            )}
          </button>
        </div>

        {/* Results */}
        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <div className="bg-gray-800 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-white mb-4">
                Flow Results {results.success && '✅'}
              </h2>
              
              {/* Steps Timeline */}
              <div className="space-y-3">
                {results.steps?.map((step: any) => (
                  <motion.div
                    key={step.step}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: step.step * 0.1 }}
                    className="flex items-start gap-4 p-3 bg-gray-700 rounded-lg"
                  >
                    <div className="mt-1">
                      {stepIcons[step.step] || <CheckCircle2 className="w-5 h-5" />}
                    </div>
                    <div className="flex-1">
                      <div className="text-white font-medium">
                        Step {step.step}: {step.action}
                      </div>
                      {step.data?.conversionPath && (
                        <div className="text-purple-400 text-sm mt-1">
                          {step.data.conversionPath}
                        </div>
                      )}
                      {step.data?.duration && (
                        <div className="text-gray-400 text-sm mt-1">
                          Duration: {step.data.duration}ms
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Metrics */}
              {results.metrics && (
                <div className="mt-6 pt-6 border-t border-gray-600">
                  <h3 className="text-white font-medium mb-3">Test Metrics</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <span className="text-gray-400 text-sm">WA Messages</span>
                      <div className="text-white font-bold text-xl">
                        {results.metrics.whatsappMessages}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">IG Conversations</span>
                      <div className="text-white font-bold text-xl">
                        {results.metrics.instagramConversations}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Duration</span>
                      <div className="text-white font-bold text-xl">
                        {(results.duration / 1000).toFixed(1)}s
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Status</span>
                      <div className="text-green-400 font-bold text-xl">
                        {results.success ? 'SUCCESS' : 'FAILED'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Phase0Tester;